import React, { useEffect } from 'react';
import { IonApp, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useAuthStore } from '@/stores/authStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { workspacesApi, setApiTokenGetter } from '@/lib/api';
import ToastContainer from '@/components/ui/ToastContainer';
import AppShell from '@/components/layout/AppShell';

// Pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import GraphPage from '@/pages/GraphPage';
import CapabilityPage from '@/pages/CapabilityPage';
import CapabilityListPage from '@/pages/CapabilityListPage';
import CollectionsPage from '@/pages/CollectionsPage';
import NotesPage from '@/pages/NotesPage';
import SearchPage from '@/pages/SearchPage';
import SettingsPage from '@/pages/SettingsPage';
import OnboardingPage from '@/pages/OnboardingPage';

// Ionic CSS
import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

setupIonicReact({
  mode: 'md',
  animated: true,
});

// ─── Strip emoji from display names ──────────────────────────────────────────

const stripEmoji = (s: string): string =>
  s.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim();

// ─── Coming Soon placeholder ──────────────────────────────────────────────────

const ComingSoon: React.FC<{ module: string; description: string }> = ({ module, description }) => (
  <AppShell>
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      <div className="card p-8 max-w-sm w-full">
        <p className="eyebrow mb-4">Coming Soon</p>
        <h1 className="text-2xl font-bold mb-3"
          style={{ fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text)' }}>
          {module}
        </h1>
        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          {description}
        </p>
      </div>
    </div>
  </AppShell>
);

// ─── Clerk → authStore bridge ─────────────────────────────────────────────────

const ClerkBridge: React.FC = () => {
  const { isSignedIn, isLoaded, getToken, signOut } = useAuth();
  const { user } = useUser();
  const login = useAuthStore((s) => s.login);
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    if (!isLoaded) return;

    if (isSignedIn && user) {
      setApiTokenGetter(() => getToken());

      getToken().then((token) => {
        if (!token) return;
        login(
          {
            id: user.id,
            email: user.primaryEmailAddress?.emailAddress ?? '',
            name: stripEmoji(user.fullName ?? user.firstName ?? '') || 'User',
            avatarUrl: user.imageUrl ?? null,
            bio: null,
            createdAt: user.createdAt?.toISOString() ?? new Date().toISOString(),
            updatedAt: user.updatedAt?.toISOString() ?? new Date().toISOString(),
            preferences: {
              theme: 'dark',
              defaultGraphLayout: 'fcose',
              emailNotifications: true,
              aiSuggestionsEnabled: true,
              autoSyncEnabled: true,
              sidebarCollapsed: false,
            },
          },
          token
        );
      });
    } else if (isLoaded && !isSignedIn) {
      setApiTokenGetter(null);
      logout();
    }
  }, [isSignedIn, isLoaded, user, getToken, login, logout, signOut]);

  return null;
};

// ─── Auth guard ───────────────────────────────────────────────────────────────

interface ProtectedRouteProps {
  component: React.ComponentType;
  path: string;
  exact?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  component: Component,
  ...rest
}) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { isSignedIn, isLoaded } = useAuth();

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isLoaded) return null;
        return isAuthenticated || isSignedIn ? (
          <Component {...(props as unknown as Record<string, unknown>)} />
        ) : (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: props.location },
            }}
          />
        );
      }}
    />
  );
};

// ─── Workspace bootstrap ──────────────────────────────────────────────────────

const WorkspaceBootstrap: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?.id);
  const userName = useAuthStore((s) => s.user?.name ?? 'My');
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces);
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace);
  const addWorkspace = useWorkspaceStore((s) => s.addWorkspace);
  const [workspaceReady, setWorkspaceReady] = React.useState(false);

  useEffect(() => {
    // Guard 1: Clerk must be fully loaded and the session active.
    // authStore.isAuthenticated can be stale (persisted) — always cross-check
    // with Clerk's own flags so we never bootstrap with a dead session.
    if (!isLoaded || !isSignedIn) return;

    // Guard 2: We need a live Clerk token before issuing any API call.
    // Even when isSignedIn=true, getToken() can still return null for a brief
    // window while the session is being hydrated. Calling the API without a
    // token gets a CORS-less 401 that looks like a network error to the browser
    // and permanently breaks the bootstrap. We await the token and prime the
    // API client before proceeding.
    getToken().then((token) => {
      if (!token) {
        // Token not yet available — Clerk will re-render; the effect re-runs.
        return;
      }

      // Prime the API client with the live getter so every subsequent call
      // uses a fresh token (handles silent refresh automatically).
      setApiTokenGetter(() => getToken());

      if (activeWorkspaceId === 'ws_offline') {
        setWorkspaces([]);
        setActiveWorkspace(null);
      }

      workspacesApi
        .list()
        .then(async (workspaces) => {
          if (workspaces.length === 0) {
            const created = await workspacesApi.create({
              name: `${(userName ?? 'My').split(' ')[0]}'s Workspace`,
              description: 'Personal capability graph',
            });
            addWorkspace(created);
            setActiveWorkspace(created.id);
          } else {
            setWorkspaces(workspaces);
            const stillValid = workspaces.some((w) => w.id === activeWorkspaceId);
            if (!stillValid || activeWorkspaceId === 'ws_offline') {
              setActiveWorkspace(workspaces[0].id);
            }
          }
        })
        .catch((err) => {
          console.error('[WorkspaceBootstrap] API error:', err);
          if (activeWorkspaceId === 'ws_offline') {
            setWorkspaces([]);
            setActiveWorkspace(null);
          }
        })
        .finally(() => {
          setWorkspaceReady(true);
        });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userId, isSignedIn, isLoaded]);

  if (isAuthenticated && isSignedIn && !workspaceReady) {
    return null;
  }

  return <>{children}</>;
};

// ─── Placeholder pages (modules not yet built) ────────────────────────────────

const ThinkingPage: React.FC = () => (
  <ComingSoon
    module="Thinking"
    description="Questions, ideas, and insights — your thinking layer. Coming soon."
  />
);

const DecisionsPage: React.FC = () => (
  <ComingSoon
    module="Decision Journal"
    description="Record decisions with full context, options considered, and expected outcomes. Close the loop when reality arrives."
  />
);

const ReviewsPage: React.FC = () => (
  <ComingSoon
    module="Reviews"
    description="Daily, weekly, and monthly reviews to reflect and recalibrate. The compound habit of self-awareness."
  />
);

const RelationshipsPage: React.FC = () => (
  <ComingSoon
    module="Relationships"
    description="People, organizations, and meetings. Your professional relationship graph."
  />
);

const ProjectsPage: React.FC = () => (
  <ComingSoon
    module="Projects"
    description="Goals, initiatives, and execution. From intention to outcome."
  />
);

// ─── App ──────────────────────────────────────────────────────────────────────

const App: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { isSignedIn, isLoaded } = useAuth();

  return (
    <IonApp>
      <IonReactRouter>
        <ClerkBridge />
        <ToastContainer />
        <WorkspaceBootstrap>
          <Switch>
            {/* Public */}
            <Route
              path="/login"
              render={() => {
                if (!isLoaded) return <LoginPage />;
                return isAuthenticated || isSignedIn
                  ? <Redirect to="/dashboard" />
                  : <LoginPage />;
              }}
            />

            {/* Protected — core */}
            <ProtectedRoute path="/onboard" component={OnboardingPage} />
            <ProtectedRoute path="/dashboard" component={DashboardPage} />
            <ProtectedRoute path="/graph" component={GraphPage} />
            <ProtectedRoute path="/capabilities/:id" component={CapabilityPage} />
            <ProtectedRoute path="/capabilities" component={CapabilityListPage} />
            <ProtectedRoute path="/collections" component={CollectionsPage} />
            <ProtectedRoute path="/search" component={SearchPage} />
            <ProtectedRoute path="/settings" component={SettingsPage} />

            {/* Protected — module placeholders */}
            <ProtectedRoute path="/notes" component={NotesPage} />
            <ProtectedRoute path="/thinking" component={ThinkingPage} />
            <ProtectedRoute path="/decisions" component={DecisionsPage} />
            <ProtectedRoute path="/reviews" component={ReviewsPage} />
            <ProtectedRoute path="/relationships" component={RelationshipsPage} />
            <ProtectedRoute path="/projects" component={ProjectsPage} />

            {/* Default */}
            <Route exact path="/">
              <Redirect to={(isAuthenticated || isSignedIn) ? '/dashboard' : '/login'} />
            </Route>

            {/* 404 fallback */}
            <Route>
              <Redirect to={isAuthenticated ? '/dashboard' : '/login'} />
            </Route>
          </Switch>
        </WorkspaceBootstrap>
      </IonReactRouter>
    </IonApp>
  );
};

export default App;
